import { useKeycloakAuth } from "@auth/KeycloakAuthProvider";
import { setLogoutInProgress } from "@auth/keycloakClient";
import ToastConstant from "@configs/react-toastify/snackbar";
import { LOCAL_STORAGE, TOAST } from "@constants/index";
import { useTheme } from "@hooks/useTheme";
import { isAuthenticated } from "@utils/auth";
import {
  clearPendingLoginErrorAndUnblockSso,
  consumeBootstrapErrorToastSlot,
  isPageReloadNavigation,
  mapBackendLoginMessageToToast,
  peekSessionBootstrapError,
} from "@utils/sessionBootstrapError";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router";

const KeycloakLogin = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const hasTriggeredLoginRef = useRef(false);
  const [loginErrorBanner, setLoginErrorBanner] = useState(null);
  const isTokenExpired = new URLSearchParams(location.search).get("expired") === "true";
  const { keycloak, loginWithKeycloak } = useKeycloakAuth();

  const getRedirectPathAfterLogin = () => {
    const saved = localStorage.getItem(LOCAL_STORAGE.REDIRECT_AFTER_LOGIN);
    if (saved && !saved.includes("/login")) {
      return saved;
    }
    const from = location.state?.from;
    const fromPath = from
      ? `${from.pathname || ""}${from.search || ""}`
      : "";
    if (fromPath && !fromPath.includes("/login")) {
      return fromPath;
    }
    return "/dashboard";
  };

  useEffect(() => {
    setLogoutInProgress(false);
  }, []);

  useEffect(() => {
    if (isPageReloadNavigation()) {
      clearPendingLoginErrorAndUnblockSso();
      setLoginErrorBanner(null);
      return;
    }

    const stored = peekSessionBootstrapError();
    if (stored === null) {
      return;
    }
    const mapped = mapBackendLoginMessageToToast(stored);
    const title = t(mapped.titleKey);
    const message = mapped.literalMessage !== undefined ? mapped.literalMessage : t(mapped.messageKey);
    setLoginErrorBanner({ title, message });
    if (consumeBootstrapErrorToastSlot()) {
      ToastConstant(TOAST.error, title, message);
    }
  }, [t]);

  useEffect(() => {
    if (keycloak?.authenticated && isAuthenticated()) {
      const redirectTo = getRedirectPathAfterLogin();
      localStorage.removeItem(LOCAL_STORAGE.REDIRECT_AFTER_LOGIN);
      navigate(redirectTo, { replace: true });
    }
  }, [keycloak, navigate, location.state]);

  useEffect(() => {
    if (!keycloak || keycloak.authenticated || hasTriggeredLoginRef.current) {
      return;
    }
    if (peekSessionBootstrapError() !== null) {
      return;
    }

    hasTriggeredLoginRef.current = true;
    const redirectPath = getRedirectPathAfterLogin();
    loginWithKeycloak({ redirectPath }).catch((error) => {
      hasTriggeredLoginRef.current = false;
      console.error("Failed to start Keycloak login redirect", error);
    });
  }, [keycloak, loginWithKeycloak]);

  const handleContinueToSso = () => {
    clearPendingLoginErrorAndUnblockSso();
    setLoginErrorBanner(null);
    hasTriggeredLoginRef.current = false;
    const redirectPath = getRedirectPathAfterLogin();
    loginWithKeycloak({ redirectPath }).catch((error) => {
      console.error("Failed to start Keycloak login redirect", error);
    });
  };

  return (
    <div className="space-y-3">
      <div className="text-center">
        <div className={`mx-auto h-12 w-12 flex items-center justify-center rounded-full ${colors.bgIconAccent}`}>
          <svg
            className={`h-8 w-8 ${colors.textActive}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
        </div>
      </div>

      {isTokenExpired && (
        <div
          className={`rounded-lg border px-3 py-2 ${colors.alertWarning}`}
          role="status"
          aria-live="polite"
        >
          <p className={`text-sm text-center ${colors.textActive}`}>{t("login.session-expired.message")}</p>
        </div>
      )}

      {loginErrorBanner && (
        <>
          <div className={`rounded-lg border px-3 py-2 ${colors.alertErrorSoft}`} role="alert">
            <p className={`text-sm font-medium ${colors.textActive}`}>{loginErrorBanner.title}</p>
            <p className={`text-sm mt-1 ${colors.textSecondary}`}>{loginErrorBanner.message}</p>
          </div>

          <div className="mt-3 flex justify-center">
            <button
              type="button"
              onClick={handleContinueToSso}
              className={`rounded-lg px-4 py-2 text-sm font-semibold shadow-sm transition-opacity hover:opacity-90 ${colors.textButton} ${colors.buttonBackgroundGradient}`}
            >
              {t("login.continue-to-sso")}
            </button>
          </div>
        </>
      )}

      <div className="text-center">
        <p className={`text-sm ${colors.textSecondary}`}>
          {loginErrorBanner ? t("login.review-error-then-continue") : t("login.redirecting-to-sso")}
        </p>
      </div>
    </div>
  );
};

export default KeycloakLogin;
